import { APIResponse, NameDataSource } from '@app/core.model';

export interface ClinicRoom {
  createdAt: string;
  weight: number;
  id: string;
  name: string;
  updatedAt: string;
}
export interface ClinicPayload {
  id?: string;
  name: string;
  address: Address;
  rooms?: ClinicRoom[];
  reviewLink?: string;
  mapLink: string;
  mobileNumber: string;
  mobileNumberSecondary?: string;
  emailAddress: string;
  companyName: string;
  companyAddress: Address;
  companyGst: string;
  companyCin: string;
  whatsapp: { phoneNumber: string };
  displayName: string;
  shortCode: string;
  showUser: boolean;
  showReceptionist: boolean;

  licence20?: {
    licenceNo: string;
    expiryDate: string;
    image?: { name: string | undefined; dataURI: string | undefined };
  };
  fssai?: {
    licenceNo: string;
    expiryDate: string;
    image?: { name: string | undefined; dataURI: string | undefined };
  };
  licence21?: {
    licenceNo: string;
    expiryDate: string;
    image?: { name: string | undefined; dataURI: string | undefined };
  };
  digitalSignature?: {
    name: string | undefined;
    dataURI?: string | undefined;
  };
  hasPharmacy: boolean;
  pharmacyMobileNumber?: string;

  posDevices?: {
    name: string;
    terminalId: string;
    merchantId: string;
    vendor?: string;
    location: number;
  }[];
}

export interface Clinic {
  showReceptionist: boolean;
  showUser: boolean;
  id: string;
  name: string;
  address: Address;
  rooms?: ClinicRoom[];
  reviewLink?: string;
  company?: {
    name?: string;
    address?: Address;
    gstNumber?: string;
    cinNumber?: string;
  };
  mobileNumber?: string;
  mobileNumberSecondary?: string;
  whatsapp?: {
    phoneNumber?: string;
  };
  mapLink?: string;
  emailAddress?: string;
  displayName?: string;
  shortCode?: string;

  posDevices?: {
    name: string;
    terminalId: string;
    merchantId: string;
    vendor?: string;
    location: number; // PosLocation enum value
  }[];
}

export interface Address {
  line1: string;
  line2: string;
  city: {
    id: string;
    name: string;
  };
  state: {
    id: string;
    name: string;
  };
  zipCode: string;
}

export interface ClinicResponse extends APIResponse, Clinic {
  clinics: Array<Clinic>;
}

export interface StateResponse extends APIResponse {
  states: Array<NameDataSource>;
}

export interface HealthCareService {
  id: string;
  name: string;
}

export interface SubCategory {
  id: string;
  name: string;
  services: HealthCareService[];
}

export interface HealthCareCategory {
  id: string;
  name: string;
  subCategories: SubCategory[];
}

export interface HealthCareCategoryResponse extends APIResponse {
  healthCareCategories: HealthCareCategory[];
}
export interface MappedClincResponse extends APIResponse {
  clinicServiceIds: string[];
}

export interface ClinicDetailResponse {
  status: boolean;
  code: string;
  message: string;
  clinic: {
    id: string;
    name: string;
    createdAt: string;
    sequentialNumber: number;
    address: {
      line1: string;
      city: {
        id: string;
        name: string;
      };
      state: {
        id: string;
        name: string;
      };
      zipCode: string;
    };
    lastInvoiceNumber: number;
    company: {
      name: string;
      gstNumber: string;
      cinNumber: string;
      address: {
        line1: string;
        line2: string;
        city: {
          id: string;
          name: string;
        };
        state: {
          id: string;
          name: string;
        };
        zipCode: string;
      };
    };
    mobileNumber: string;
    whatsapp: {
      phoneNumber: string;
    };
    mapLink: string;
    emailAddress: string;
    rooms: Array<{
      id: string;
      name: string;
      weight: number;
      createdAt: string;
      updatedAt: string;
    }>;
    displayName: string;
    showReceptionist: boolean;
    showUser: boolean;
    licence20: Licence;
    licence21: Licence;
    fssai: Licence;
    digitalSignature: ImageDetail;
    digitalSignatureUrl?: string;
    pharmacyMobileNumber: string;
    panNumber: string;
    hasPharmacy: boolean;
    prescriptionPdfAssets?: PrescriptionPdfAssets;

    posDevices?: Array<{
      id: string;
      name: string;
      terminalId: string;
      merchantId: string;
      location: number;
      vendor: string;
      createdAt: string;
      clinic: {
        id: string;
        name: string;
      };
    }>;
  };
}

export interface ClinicLetterheadPayload {
  clinicId: string;
  letterheadName: string;
  headerImage: { name: string; dataURI: string } | null;
  footerImage: { name: string; dataURI: string } | null;
  watermarkImage: { name: string; dataURI: string } | null;
}

export interface PrescriptionPdfAssetsPayload {
  logo?: UploadedImageDetail;
  sideImage?: UploadedImageDetail;
  bottomImage?: UploadedImageDetail;
  footerText: string;
}

export interface PrescriptionPdfAssets {
  logo?: StoredImageDetail | null;
  sideImage?: StoredImageDetail | null;
  bottomImage?: StoredImageDetail | null;
  footerText?: string;
  logoUrl?: string;
  sideImageUrl?: string;
  bottomImageUrl?: string;
}

export type UploadedImageDetail = { name: string; dataURI: string };
export type PrescriptionPdfAssetValue =
  | UploadedImageDetail
  | StoredImageDetail
  | null;

export interface Licence {
  licenceNo: string;
  expiryDate: string;
  image: ImageDetail;
  imageUrl: string;
}

interface ImageDetail {
  name: string;
  mimeType: string;
  originalFileName: string;
}

export interface StoredImageDetail extends ImageDetail {
  id?: string;
}

export interface SubStore {
  id: string;
  name: string;
  description: string;
  clinic?: {
    id: string;
    name: string;
  };
  activated?: boolean;
  createdAt: string;
  updatedAt?: string;
  creator: {
    id: string;
    name: string;
    type?: number;
  };
  updater?: {
    id: string;
    name: string;
    type?: number;
  };
}

export interface SubStoreResponse extends APIResponse {
  substores: SubStore[];
  count: number;
}

export interface SubStorePayload {
  name: string;
  description: string;
}
